import React, { useReducer } from 'react';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';

import Box from '@g4/boneyard/lib/Box';
import { Row, Col } from '@g4/boneyard/lib/Grid';
import { Media } from '@g4/boneyard/lib/Media';
import Text from '@g4/boneyard/lib/Text';
import VerticalSpacing from '@g4/boneyard/lib/VerticalSpacing';

import { SlideToggle } from '../Boneyard';
import RebrandedExpando from '../RebrandedExpando';
import { InteractiveLabel } from '../Boneyard/Expando/Expando';

import FooterLink from './FooterLink';
import ConditionalWrap from '../ConditionalWrap';

const propTypes = {
  theme: PropTypes.object,
  bottomMenu: PropTypes.arrayOf(PropTypes.object),
  mediaMenu: PropTypes.arrayOf(PropTypes.object),
};

const StyledHeader = styled.h3`
  text-align: center;
  ${props => props.theme.query.sm} {
    text-align: left;
  }
`;

const ExpandoGroup = styled.div`
  > ${/* sc-selector */ RebrandedExpando}
    ${/* sc-selector */ InteractiveLabel} {
    border-top: 0;
    border-bottom: 2px solid ${props => props.theme.colors.hob.expando.border};
  }
`;

const BottomMenu = ({ theme, id, title, items, action, state }) => {
  return (
    <Media>
      {mq => (
        <Box
          d="flex"
          direction="column"
          alignItems={mq.lt('sm') ? 'center' : 'flex-start'}
        >
          <VerticalSpacing size={{ xs: 3, sm: 5, lg: 8 }} />
          <Box>
            <StyledHeader onClick={() => action(id)}>
              <Text
                size={16}
                weight={500}
                color={theme.colors.hob.footer.title}
              >
                {title}
              </Text>
            </StyledHeader>
            <ConditionalWrap
              condition={mq.lt('sm')}
              wrap={children => (
                <SlideToggle
                  label={title}
                  open={state['visible' + id]}
                  onToggle={() => action(id)}
                  statusIndicatorIconAltAttribute="statusIndicatorIconAltAttribute"
                >
                  {children}
                </SlideToggle>
              )}
            >
              <VerticalSpacing size={2} />
              <Box>
                {items &&
                  items.map((item, idx) => (
                    <FooterLink key={idx} item={item} mediaMenu={false} />
                  ))}
              </Box>

              <VerticalSpacing size={{ xs: 2, md: 0 }} />
            </ConditionalWrap>
          </Box>
        </Box>
      )}
    </Media>
  );
};

BottomMenu.propTypes = {
  theme: PropTypes.object,
  id: PropTypes.string,
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  action: PropTypes.func,
  state: PropTypes.object,
};

const MediaMenu = ({ theme, mediaMenu, action, state }) => (
  <Media>
    {mq => (
      <Box
        d="flex"
        direction="column"
        alignItems={mq.lt('sm') ? 'center' : 'flex-start'}
      >
        <VerticalSpacing size={mq.lt('sm') ? 3 : 8} />
        <StyledHeader onClick={() => action('Media')}>
          <Text size={16} weight={500} color={theme.colors.hob.footer.title}>
            Follow Us
          </Text>
        </StyledHeader>
        <ConditionalWrap
          condition={mq.lt('sm')}
          wrap={children => (
            <SlideToggle
              label={'Follow Us'}
              open={state.visibleMedia}
              onToggle={() => action('Media')}
              statusIndicatorIconAltAttribute="statusIndicatorIconAltAttribute"
            >
              {children}
            </SlideToggle>
          )}
        >
          <VerticalSpacing size={2} />
          <Box
            d="flex"
            direction={
              mq.lt('sm')
                ? 'column'
                : mq.gte('sm') && mq.lt('lg')
                ? 'row'
                : 'column'
            }
          >
            {mediaMenu.map((item, idx) => (
              <FooterLink key={idx} item={item} mediaMenu={true} />
            ))}
          </Box>
        </ConditionalWrap>
      </Box>
    )}
  </Media>
);

MediaMenu.propTypes = {
  theme: PropTypes.object,
  mediaMenu: PropTypes.arrayOf(PropTypes.object),
  action: PropTypes.func,
  state: PropTypes.object,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        ['visible' + action.payload]: !state['visible' + action.payload],
      };
    default:
      throw new Error();
  }
};

const FooterMenu = ({ theme, bottomMenu, mediaMenu }) => {
  const initialState = Object.assign(
    {
      visibleMedia: false,
    },
    ...bottomMenu.map(item => ({ ['visible' + item['id']]: false }))
  );

  const [state, dispatch] = useReducer(reducer, initialState);
  const toggleIt = id => {
    dispatch({ type: 'toggle', payload: id });
  };
  return (
    <ExpandoGroup>
      <Media>
        {mq => (
          <Row
            gutter={10}
            type={mq.gte('md') ? 'flex' : undefined}
            justify="space-between"
          >
            {bottomMenu &&
              bottomMenu.length &&
              bottomMenu.map(({ id, title, items }, index) =>
                mq.lte('sm') ? (
                  <Col key={id} md={24} lg={4}>
                    <BottomMenu
                      theme={theme}
                      id={id}
                      title={title}
                      items={items}
                      state={state}
                      action={toggleIt}
                    />
                  </Col>
                ) : (
                  <Col
                    key={id}
                    md={24}
                    lg={4}
                    {...(index === 0 && mq.gte('lg') && { offset: 2 })}
                  >
                    <BottomMenu
                      theme={theme}
                      id={id}
                      title={title}
                      items={items}
                    />
                  </Col>
                )
              )}
            {mediaMenu && mediaMenu.length !== 0 && (
              <Col md={24} lg={4}>
                <MediaMenu
                  theme={theme}
                  mediaMenu={mediaMenu}
                  state={state}
                  action={toggleIt}
                />
              </Col>
            )}
          </Row>
        )}
      </Media>
    </ExpandoGroup>
  );
};

FooterMenu.propTypes = propTypes;

export default withTheme(FooterMenu);
